import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";

import { registerStyles } from "../styles/register-styles.js";
import { schreibeTitel, titelAusPfad } from "../seitentitel/seitentitel.js";

/**
 * Der Seitenkopf — Einfassung oben, später der Weg zurück.
 *
 * Gedacht wie der Kopf eines DIN-A4-Blattes: eine ruhige Zeile mit einer
 * Haarlinie darunter, links die Einordnung, rechts Platz für später. KEINE
 * Überschrift — weder optisch noch im Markup. Die Seite behält ihre eigenen
 * Titel; dieses Element sagt nur, wo man ist.
 *
 * Deshalb ist es `<nav>` mit einer Liste und nicht `<h1>`: Ein Titel, in dem
 * Links auf andere Seiten stehen, zerlegt die Dokumentgliederung, und
 * Screenreader läsen die Vorfahren als Teil des Seitennamens vor.
 *
 * EIN Element ist kein Weg, sondern eine Beschriftung — es bekommt keinen Link
 * und trägt `aria-current="page"`. Erst ab zwei Elementen werden die vorderen
 * zu Links, getrennt durch `›`.
 *
 * NAVIGATION LÄUFT ÜBER EIN EREIGNIS, nicht über den Router: `route-event`,
 * genau wie `pd-menu` es in `@progressive-development/pd-page` tut. Damit ist
 * nichts zu verdrahten — `PdSpaHelper` hört dieses Ereignis bereits ab und
 * routet; das Element kennt den Router nicht. Der `<a href>` bleibt trotzdem
 * echt, damit Mittelklick, neuer Tab und Suchmaschinen funktionieren; der
 * Klick-Handler nimmt ihn nur zurück.
 *
 * ZIEHT SPÄTER NACH `pd-page` UM. Dort liegen `pd-site-header`, `pd-menu` und
 * `pd-footer` — dieses Element gehört in dieselbe Familie. Es entsteht
 * trotzdem zuerst hier: Solange es einen einzigen Aufrufer mit einem einzigen
 * Element gibt, verrät nichts, wie die API aussehen muss (Trennzeichen,
 * Kürzung langer letzter Elemente, rechte Seite). Mit Kapitel 2 kommen die
 * echten Aufrufer, danach der Umzug — `route-event` heißt schon jetzt so,
 * damit sich an den Aufrufern nichts ändert.
 */

/** Eine Station des Weges. Ohne `route` ist sie nicht anklickbar. */
export interface SeitenkopfStation {
  name: string;
  route?: string;
}

@customElement("app-seitenkopf")
@localized()
export class AppSeitenkopf extends LitElement {
  /** Von grob nach fein; das letzte Element ist die aktuelle Seite. */
  @property({ attribute: false }) pfad: SeitenkopfStation[] = [];

  static override styles = [
    registerStyles,
    css`
      :host {
        display: block;
      }
      /*
       * Die Linie läuft über die ganze Zeile, nicht nur unter den Text — das
       * ist der Unterschied zwischen einem Seitenkopf und einer Beschriftung.
       * Sie endet bündig mit dem Inhalt darunter, damit die Seite eine Kante
       * bekommt und keine zweite Werkzeugleiste.
       */
      .kopf {
        max-width: var(--portal-content-width);
        margin: 0 auto;
        padding: var(--portal-space-3) var(--portal-gutter);
        border-bottom: 1px solid var(--portal-linie);
        display: flex;
        align-items: baseline;
        gap: var(--portal-space-4);
      }
      nav {
        min-width: 0;
      }
      ol {
        margin: 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: var(--portal-space-2);
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
        text-transform: uppercase;
        letter-spacing: var(--portal-tracking-caps);
      }
      /* Die aktuelle Seite steht etwas fester als der Weg dorthin. */
      li[aria-current="page"] {
        color: var(--portal-ink);
      }
      a {
        color: inherit;
        text-decoration: none;
      }
      a:hover {
        color: var(--portal-akzent);
        text-decoration: underline;
      }
      .trenner {
        color: var(--portal-ink-2);
        opacity: 0.6;
      }
      /* Rechte Seite: heute leer, später Aktionen oder Stand. */
      .rechts {
        margin-left: auto;
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
      }
    `,
  ];

  /**
   * Die Brotkrume kennt den Gegenstand — also ist hier der Ort, an dem der
   * Dokumenttitel seinen Namen bekommt („Drucksache 3541/2025 ·
   * Entscheidungen"), ohne dass eine Seite es wissen muss. Die Shell hat
   * vorher den Begriff der Route gesetzt; hier wird verfeinert.
   *
   * Nur verbunden: Eine Seite, die nach dem Wegklicken noch ihre Daten
   * bekommt, rendert ihre Brotkrume trotzdem — und dürfte den Titel der
   * Seite, auf der man inzwischen ist, nicht überschreiben.
   */
  protected override updated(geaendert: PropertyValues): void {
    if (geaendert.has("pfad") && this.pfad.length > 0 && this.isConnected) {
      schreibeTitel(titelAusPfad(this.pfad));
    }
  }

  override render() {
    if (this.pfad.length === 0) return nothing;

    return html`
      <div class="kopf">
        <nav aria-label=${msg("Seitenpfad", { id: "app.seitenkopf.label" })}>
          <ol>
            ${this.pfad.map((station, i) => this._renderStation(station, i))}
          </ol>
        </nav>
        <span class="rechts"><slot name="rechts"></slot></span>
      </div>
    `;
  }

  private _renderStation(station: SeitenkopfStation, i: number) {
    const letzte = i === this.pfad.length - 1;
    // Die letzte Station ist die Seite selbst — nie ein Link, auch nicht mit
    // Route: Ein Link auf die eigene Seite ist eine Sackgasse mit Cursor.
    const inhalt =
      letzte || !station.route
        ? html`${station.name}`
        : html`<a
            href=${station.route}
            @click=${(e: MouseEvent) => this._navigiere(e, station.route!)}
            >${station.name}</a
          >`;

    return html`
      ${i > 0 ? html`<li class="trenner" aria-hidden="true">›</li>` : nothing}
      <!-- "false" ist der gültige ARIA-Wert für „nicht die aktuelle Seite". -->
      <li aria-current=${letzte ? "page" : "false"}>${inhalt}</li>
    `;
  }

  /**
   * Meldet den Wunsch, nicht die Tat.
   *
   * Modifier-Klicks bleiben unangetastet — wer mit Strg oder Mittelklick
   * öffnet, will einen neuen Tab und keinen Seitenwechsel im aktuellen.
   */
  private _navigiere(e: MouseEvent, route: string): void {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    this.dispatchEvent(
      new CustomEvent("route-event", { detail: { route }, bubbles: true, composed: true }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-seitenkopf": AppSeitenkopf;
  }
}
